import { NetworkChip } from '@mdvip/shared/components/blocks/NetworkChip';
import {
  ResponsiveModal,
  ResponsiveModalBody,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
} from '@mdvip/shared/components/blocks/ResponsiveModal';
import { useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useEditBankAccount } from '@/hooks/payments/bankAccount/useEditBankAccount';
import { useEditBankForm } from '@/hooks/payments/bankAccount/useEditBankForm';
import { useMemberAutoPaySummary } from '@/hooks/payments/memberInfo/useMemberAutoPaySummary';
import type { BankAccountAccount } from '@/lib/payments/bankAccountAccount.types';
import { toSavedAddress } from '@/lib/payments/paymentAccount.format';
import type { BillingAddressValues } from '@/lib/payments/paymentAccount.types';
import { BillingInformationSection } from './BillingInformationSection';
import { EditModalFooter } from './EditModalFooter';
import { EditReviewScreen } from './EditReviewScreen';
import { AutoPayCheckbox, HsaRadioField, MakeDefaultCheckbox, SelectField } from './fields';

interface EditBankAccountModalProps {
  /** The account being edited; `null` closes the modal. Controlled like EditCreditCardModal. */
  account: BankAccountAccount | null;
  onClose: () => void;
  /** Called after a successful save — the caller decides toast/close, same contract as card Edit. */
  onSuccess?: () => void;
}

/**
 * Edit Payment Account modal — bank account. Reuses the same shared components as the credit card
 * Edit modal (fields.tsx, BillingInformationSection, EditReviewScreen) and screen-switch structure.
 * Unlike the card variant, there is no analogous "locked while expired" state — bank accounts have
 * no expiry field, so HSA/Default/AutoPay/billing-Change are never disabled here.
 */
export function EditBankAccountModal({ account, onClose, onSuccess }: EditBankAccountModalProps) {
  return (
    <ResponsiveModal open={account !== null} onOpenChange={(open) => !open && onClose()} className="sm:max-w-2xl">
      {account && (
        <EditBankAccountModalContent key={account.id} account={account} onClose={onClose} onSuccess={onSuccess} />
      )}
    </ResponsiveModal>
  );
}

interface EditBankAccountModalContentProps {
  account: BankAccountAccount;
  onClose: () => void;
  onSuccess?: () => void;
}

function EditBankAccountModalContent({ account, onClose, onSuccess }: EditBankAccountModalContentProps) {
  const { t } = useTranslation('members');
  const [screen, setScreen] = useState<'form' | 'confirm'>('form');
  const [billingFormOpen, setBillingFormOpen] = useState(false);

  const savedAddress: BillingAddressValues = toSavedAddress(account);
  const form = useEditBankForm({ account, billingRequired: billingFormOpen });
  const { data: autoPaySummary, isLoading: isAutoPaySummaryLoading } = useMemberAutoPaySummary();
  const bankMutation = useEditBankAccount({ onSuccess });
  const isSubmitting = bankMutation.isPending;

  const handleToggleBillingForm = () => {
    if (billingFormOpen) form.resetField('billing');
    setBillingFormOpen((isOpen) => !isOpen);
  };

  const handleReview = form.handleSubmit(() => setScreen('confirm'));

  // The billing address that will be submitted: the entered sub-form when the member opened
  // "Change", otherwise the account's own saved address (mirrors card Edit's effectiveBilling).
  const effectiveBilling: BillingAddressValues = billingFormOpen ? form.getValues('billing') : savedAddress;

  const handleConfirmSubmit = () => {
    const values = form.getValues();
    bankMutation.submit({
      id: account.id,
      accountType: values.bank.accountType,
      isHsa: values.bank.isHsa,
      makeDefault: values.bank.makeDefault,
      autoPay: values.bank.autoPay,
      billing: effectiveBilling,
    });
  };

  return (
    <FormProvider {...form}>
      <ResponsiveModalHeader>
        <ResponsiveModalTitle>{t('payments.editAccount.title')}</ResponsiveModalTitle>
      </ResponsiveModalHeader>

      <ResponsiveModalBody>
        {screen === 'form' ? (
          <div className="space-y-6">
            <div className="flex items-center gap-3 rounded-lg border bg-muted/40 p-3.5">
              <NetworkChip kind="bank" />
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-foreground">
                  {`${account.bankName} ···· ${account.last6}`}
                </div>
                <div className="text-xs text-muted-foreground">{t('payments.editAccount.bankLabel')}</div>
              </div>
            </div>

            <SelectField
              name="bank.accountType"
              label={t('payments.addAccount.accountType')}
              options={[
                { value: 'checking', label: t('payments.addAccount.accountTypeOptions.checking') },
                { value: 'savings', label: t('payments.addAccount.accountTypeOptions.savings') },
                { value: 'other', label: t('payments.addAccount.accountTypeOptions.other') },
              ]}
            />

            <HsaRadioField scope="bank" />
            <div className="space-y-2.5">
              <MakeDefaultCheckbox scope="bank" isCurrentDefault={account.isDefault} />
              <AutoPayCheckbox scope="bank" autoPayEnrolled={autoPaySummary?.autoPayEnrolled ?? false} />
            </div>

            <BillingInformationSection
              accountKind="bank"
              savedAddress={savedAddress}
              formOpen={billingFormOpen}
              onToggleForm={handleToggleBillingForm}
              addNewLabel={t('payments.editAccount.billing.change')}
              addNewIcon="pencil"
            />
          </div>
        ) : (
          <EditReviewScreen
            scope="bank"
            icon="landmark"
            headline={`${account.bankName} ···· ${account.last6}`}
            caption={t(`payments.pages.paymentAccounts.bankAccounts.accountType.${form.getValues('bank.accountType')}`)}
            autoPayEnrolled={autoPaySummary?.autoPayEnrolled ?? false}
            autoPayAmount={autoPaySummary?.autoPayAmount ?? null}
            autoPayPlan={autoPaySummary?.autoPayPlan ?? null}
            billing={effectiveBilling}
          />
        )}
      </ResponsiveModalBody>

      <EditModalFooter
        screen={screen}
        onCancel={onClose}
        onReview={handleReview}
        reviewDisabled={isAutoPaySummaryLoading}
        onMakeChanges={() => setScreen('form')}
        onConfirm={handleConfirmSubmit}
        isSubmitting={isSubmitting}
      />
    </FormProvider>
  );
}
